import * as React from "react";
import {
  Title2,
  Body1,
  Dropdown,
  Option,
  Button,
  makeStyles,
  Spinner,
  tokens,
} from "@fluentui/react-components";
import { getOrganizations } from "../services/api";
import { Organization } from "../types";

const LAST_ORG_KEY = "ddsign_last_org";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  header: {
    marginBottom: "8px",
  },
});

interface Props {
  onSelect: (org: Organization) => void;
}

const OrganizationSelector: React.FC<Props> = ({ onSelect }) => {
  const styles = useStyles();
  const [orgs, setOrgs] = React.useState<Organization[]>([]);
  const [selectedId, setSelectedId] = React.useState<string>("");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    loadOrgs();
  }, []);

  const loadOrgs = async () => {
    try {
      const data = await getOrganizations();
      setOrgs(data);

      if (data.length === 1) {
        // Auto-select if only one org
        onSelect(data[0]);
        return;
      }

      // Restore last selected org
      const lastOrgId = localStorage.getItem(LAST_ORG_KEY);
      if (lastOrgId && data.find((o) => o.id === lastOrgId)) {
        setSelectedId(lastOrgId);
      }
    } catch (err: any) {
      setError("Failed to load organizations.");
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    const org = orgs.find((o) => o.id === selectedId);
    if (org) {
      localStorage.setItem(LAST_ORG_KEY, org.id);
      onSelect(org);
    }
  };

  if (loading) {
    return <Spinner label="Loading organizations..." />;
  }

  if (error) {
    return <Body1 style={{ color: tokens.colorPaletteRedForeground1 }}>{error}</Body1>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title2>Select Organization</Title2>
        <Body1>Choose which organization to send from.</Body1>
      </div>

      <Dropdown
        placeholder="Select an organization"
        value={orgs.find((o) => o.id === selectedId)?.name || ""}
        onOptionSelect={(_, data) => setSelectedId(data.optionValue as string)}
      >
        {orgs.map((org) => (
          <Option key={org.id} value={org.id}>
            {org.name}
          </Option>
        ))}
      </Dropdown>

      <Button appearance="primary" onClick={handleContinue} disabled={!selectedId}>
        Continue
      </Button>
    </div>
  );
};

export default OrganizationSelector;
